(() => {
  const configuration = window.freeGiftStorefrontConfiguration || { mappings: [] };
  const mappings = Array.isArray(configuration.mappings) ? configuration.mappings : [];
  if (!mappings.length) return;

  const byFreeVariant = new Map(mappings.map((mapping) => [Number(mapping.freeVariantId), mapping]));
  const byProduct = new Map(mappings.map((mapping) => [Number(mapping.productId), mapping]));
  let applying = false;
  const cssEscape = (value) => window.CSS?.escape ? window.CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');

  const debug = (...args) => {
    if (configuration.debug) console.info('[Free gifts] Storefront guard:', ...args);
  };

  const resolveVariantId = (variantId) => {
    const mapping = byFreeVariant.get(Number(variantId));
    return mapping ? Number(mapping.paidVariantId) : Number(variantId);
  };

  const mappingForUrl = (urlValue) => {
    try {
      const url = new URL(urlValue, window.location.origin);
      return mappings.find((mapping) => mapping.productUrl &&
        (url.pathname === mapping.productUrl || url.pathname.endsWith(mapping.productUrl)));
    } catch (error) {
      return null;
    }
  };

  const paidProductUrl = (mapping, sourceUrl = mapping.productUrl) => {
    const url = new URL(sourceUrl || window.location.href, window.location.origin);
    url.searchParams.set('variant', String(mapping.paidVariantId));
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const sanitizeVariantInput = (input) => {
    if (!input) return false;
    const currentId = Number(input.value);
    const paidId = resolveVariantId(currentId);
    if (!(paidId > 0) || paidId === currentId) return false;
    input.value = String(paidId);
    input.setAttribute('value', String(paidId));
    debug('Replaced a storefront free variant input.', { freeVariantId: currentId, paidVariantId: paidId });
    return true;
  };

  const sanitizeForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    return sanitizeVariantInput(form.querySelector('[name="id"]'));
  };

  const sanitizeLink = (link) => {
    const mapping = mappingForUrl(link.href);
    if (!mapping) return;
    const url = new URL(link.href, window.location.origin);
    const selectedVariantId = Number(url.searchParams.get('variant'));
    if (selectedVariantId === Number(mapping.paidVariantId)) return;
    link.href = paidProductUrl(mapping, link.href);
  };

  const removeFreeOnlyOptionValues = (picker, mapping) => {
    const freeOptions = Array.isArray(mapping.freeOptions) ? mapping.freeOptions : [];
    const paidOptions = Array.isArray(mapping.paidOptions) ? mapping.paidOptions : [];
    let changedControl = null;

    freeOptions.forEach((freeValue, index) => {
      const paidValue = paidOptions[index];
      if (freeValue === paidValue || paidValue == null) return;

      const fieldset = picker.querySelectorAll('fieldset')[index];
      (fieldset || picker).querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.value !== String(freeValue)) return;
        const label = input.id ? picker.querySelector(`label[for="${cssEscape(input.id)}"]`) : null;
        if (input.checked) {
          const paidInput = Array.from((fieldset || picker).querySelectorAll('input[type="radio"]'))
            .find((candidate) => candidate.name === input.name && candidate.value === String(paidValue));
          if (paidInput) {
            paidInput.checked = true;
            changedControl = paidInput;
          }
        }
        label?.remove();
        input.remove();
      });

      const select = picker.querySelectorAll('select')[index];
      if (select) {
        const option = Array.from(select.options).find((candidate) => candidate.value === String(freeValue));
        if (option) {
          if (option.selected) {
            select.value = String(paidValue);
            changedControl = select;
          }
          option.remove();
        }
      }
    });

    return changedControl;
  };

  const sanitizePicker = (picker) => {
    const dataElement = picker.querySelector('script[type="application/json"]');
    if (!dataElement) return;

    let variants;
    try {
      variants = JSON.parse(dataElement.textContent || '[]');
    } catch (error) {
      return;
    }

    const mapping = mappingForUrl(picker.dataset.url) || mappings.find((candidate) =>
      variants.some((variant) => Number(variant.id) === Number(candidate.freeVariantId))
    );
    if (!mapping) return;

    const allowedVariants = variants.filter((variant) => Number(variant.id) !== Number(mapping.freeVariantId));
    if (allowedVariants.length !== variants.length) {
      dataElement.textContent = JSON.stringify(allowedVariants);
      picker.variantData = allowedVariants;
    }

    const changedControl = removeFreeOnlyOptionValues(picker, mapping);
    const sectionId = picker.dataset.section;
    document.querySelectorAll(`#product-form-${cssEscape(sectionId || '')} [name="id"], #product-form-installment [name="id"]`)
      .forEach((input) => {
        if (Number(input.value) === Number(mapping.freeVariantId) || Number(input.value) <= 0) {
          input.value = String(mapping.paidVariantId);
          input.setAttribute('value', String(mapping.paidVariantId));
        }
      });

    if (changedControl) {
      window.setTimeout(() => changedControl.dispatchEvent(new Event('change', { bubbles: true })), 0);
    }
    debug('Removed a free gift variant from the product picker.', mapping);
  };

  const apply = (root = document) => {
    if (applying) return;
    applying = true;
    try {
      const scope = root instanceof Element || root instanceof Document ? root : document;
      if (scope instanceof HTMLFormElement) sanitizeForm(scope);
      if (scope instanceof HTMLAnchorElement) sanitizeLink(scope);
      if (scope.matches?.('variant-radios, variant-selects')) sanitizePicker(scope);
      scope.querySelectorAll('form').forEach(sanitizeForm);
      scope.querySelectorAll('a[href]').forEach(sanitizeLink);
      scope.querySelectorAll('variant-radios, variant-selects').forEach(sanitizePicker);
    } finally {
      applying = false;
    }
  };

  window.FreeGiftStorefrontGuard = {
    mappings,
    resolveVariantId,
    mappingForProductId: (productId) => byProduct.get(Number(productId)) || null,
    paidProductUrl,
    sanitizeForm,
    apply
  };

  document.addEventListener('submit', (event) => sanitizeForm(event.target), true);
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const actionable = event.target.closest('button, input, a');
    if (actionable?.form) sanitizeForm(actionable.form);
  }, true);
  document.addEventListener('shopify:section:load', (event) => apply(event.target));

  const observer = new MutationObserver((mutations) => {
    const roots = mutations.flatMap((mutation) => Array.from(mutation.addedNodes))
      .filter((node) => node instanceof Element);
    if (roots.length) window.requestAnimationFrame(() => roots.forEach(apply));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      apply();
      observer.observe(document.body, { childList: true, subtree: true });
    }, { once: true });
  } else {
    apply();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
